import React from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { cn } from '../../../../../lib/utils';
import { getSeverityLabel } from './auditTrailConstants';

/** Matches project dashboard + Admin tab styling (hero-red active underline). */
export const AUDIT_TAB_LIST_CLASS =
  'h-auto w-full justify-start gap-6 md:gap-8 bg-transparent rounded-none p-0 border-0';

export const AUDIT_TAB_TRIGGER_CLASS =
  'relative inline-flex flex-shrink-0 items-center gap-1.5 rounded-none border-b-[3px] border-transparent px-0.5 pb-3 pt-1.5 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue whitespace-nowrap shadow-none data-[state=active]:border-hero-red data-[state=active]:text-hero-red data-[state=active]:shadow-none bg-transparent';

export function SeverityBadge({ severity }: { severity: string }) {
  const normalized = severity?.toUpperCase() ?? 'INFO';
  const className =
    normalized === 'ERROR' || normalized === 'ERR'
      ? 'text-readable-red bg-[rgba(196,0,31,0.08)]'
      : normalized === 'WARN' || normalized === 'WARNING'
        ? 'text-[#B38600] bg-[rgba(230,161,0,0.08)]'
        : 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]';

  return (
    <span
      className={cn(
        'inline-flex px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide',
        className
      )}
      title={severity}
    >
      {getSeverityLabel(severity)}
    </span>
  );
}

export function AuditLoadingState({ message = 'Loading…' }: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-heritage-grey">
      <Loader2 className="h-8 w-8 animate-spin mb-3" />
      <p className="text-[13px]">{message}</p>
    </div>
  );
}

export function AuditEmptyState({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-[#E2E5EA] bg-white/60 p-12 text-center">
      <p className="font-heading text-[14px] font-semibold text-blueprint-blue">{title}</p>
      {description ? (
        <p className="mx-auto mt-2 max-w-md text-[13px] text-heritage-grey">{description}</p>
      ) : null}
    </div>
  );
}

export function AuditErrorBanner({
  message,
  onRetry,
  unavailable,
}: {
  message: string;
  onRetry?: () => void;
  unavailable?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border p-4 text-[13px]',
        unavailable
          ? 'border-[#E2E5EA] bg-[rgba(230,161,0,0.06)] text-[#8A6500]'
          : 'border-readable-red/30 bg-[rgba(196,0,31,0.06)] text-readable-red'
      )}
    >
      <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="font-medium">{unavailable ? 'API not available' : 'Could not load data'}</p>
        <p className="mt-1 text-heritage-grey">{message}</p>
      </div>
      {onRetry ? (
        <Button type="button" variant="outline" size="sm" className="h-8 shrink-0" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </div>
  );
}

export function severityBorderClass(severity: string): string {
  const s = severity?.toUpperCase() ?? 'INFO';
  if (s === 'ERR' || s === 'ERROR') return 'border-readable-red';
  if (s === 'WARN' || s === 'WARNING') return 'border-[#B38600]';
  return 'border-blueprint-blue';
}

export function EntityTypeBadge({ type }: { type: string }) {
  return (
    <span className="inline-flex w-fit px-1.5 py-0.5 rounded text-[10px] font-medium bg-page-bg text-heritage-grey border border-[#EEF0F3]">
      {type}
    </span>
  );
}
